/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  NoticeDocumentType,
  StatusCode,
} from '../../../../../../../../../../api/enumerations';
import { createNewCriteria } from '../../../../../../../../../../api/theHive/criteriaGroups';
import {
  CriteriaData,
  DocumentData,
} from '../../../../../../../../../../api/theHive/criteriaGroups/types';
import { FlexReverseBox } from '../../../../../../../../../../components/Sections/Rooms/styles';
import { CloseIcon } from '../../../../../../../../../../components/UI/Button';
import {
  ColoredCircularProgress,
  DialogTitle,
  textFieldDefaultStyle,
} from '../../../../../../../../../../components/UI/Typography';
import {
  IconAddMS,
  IconCloseMS,
  IconDeleteMS,
} from '../../../../../../../../../../constants/icons';
import { GlobalContext } from '../../../../../../../../../../context/global';
import useErrorMessage from '../../../../../../../../../../hooks/useErrorMessage';
import { SaveButton } from '../../../../styles';
import { ActionButton, AddTypologyGroup } from '../../styles';
import { AccordionCriteria } from '../AccordionCriteria';
import {
  CriteriasListBox,
  CriteriaText,
  DialogContainer,
  OutlineButton,
  StyledDialog,
  SubmitButton,
  TextTypography,
  UploadBox,
} from './styles';

interface CriteriasDialogProps {
  criteriasData: CriteriaData[];
  selectedCriterias: number[];
  handleSelection: (criteriaIds: number[]) => void;
  loading: boolean;
  disable: boolean;
  activity: number;
  getDefaultCriteriasCallback: () => Promise<void>;
}

export default function CriteriasDialog({
  criteriasData,
  selectedCriterias,
  handleSelection,
  loading,
  disable,
  activity,
  getDefaultCriteriasCallback,
}: CriteriasDialogProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [localSelected, setLocalSelected] = useState<number[]>([]);
  const [description, setDescription] = useState('');
  const [showNewCriteria, setShowNewCriteria] = useState(false);
  const [documentName, setDocumentName] = useState<DocumentData[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();
  const { notice } = useParams();
  const noticeId = parseInt(notice as string, 10);

  const resetNewCriteria = (): void => {
    setShowNewCriteria(false);
    setDescription('');
    setDocumentName([]);
  };
  const handleOpen = (): void => setOpen(true);
  const handleClose = (): void => {
    setOpen(false);
    resetNewCriteria();
  };

  const toggleSelection = (id: number): void => {
    setLocalSelected((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }

      return [...prev, id].sort((a, b) => a - b);
    });
  };

  useEffect(() => {
    setLocalSelected(selectedCriterias);
  }, [selectedCriterias]);

  const handleConfirm = (): void => {
    handleClose();
    handleSelection(localSelected);
  };

  const handleSubmit = async (): Promise<void> => {
    if (description === '') {
      setSnackbarMessage('Adicione uma descrição para o critério.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    // Cada anexo é um "envie um arquivo" identificado pelo nome. O nome não pode
    // repetir no mesmo critério (o backend distingue os anexos pelo nome).
    const cleanedDocs: DocumentData[] = documentName
      .filter((doc) => doc.name.trim() !== '')
      .map((doc) => ({
        name: doc.name.trim(),
        document_type: NoticeDocumentType.OTHERS,
      }));

    const names = cleanedDocs.map((doc) => doc.name.toLowerCase());
    if (names.length !== new Set(names).size) {
      setSnackbarMessage('Não repita o nome de um anexo no mesmo critério.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    setSubmitLoading(true);

    const newCriteria = {
      description,
      documents: cleanedDocs,
      notice_id: noticeId,
      // sem "conceito de espaço": só envia activity se houver (>0)
      ...(activity ? { activity } : {}),
    };

    try {
      const response = await createNewCriteria(newCriteria);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setSnackbarMessage('Documento solicitado com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      resetNewCriteria();
      getDefaultCriteriasCallback();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <AddTypologyGroup
        disabled={disable}
        onClick={handleOpen}
        sx={{ marginTop: 2 }}
      >
        {loading ? (
          <ColoredCircularProgress size={18} />
        ) : (
          <>{IconAddMS} critérios</>
        )}
      </AddTypologyGroup>
      <StyledDialog
        open={open}
        onClose={handleClose}
        aria-labelledby="select criterias group"
      >
        <DialogContainer>
          <CloseIcon onClick={handleClose}>{IconCloseMS}</CloseIcon>
          <DialogTitle>Adicionar critérios</DialogTitle>
          <CriteriasListBox>
            <TextTypography>Selecione o(s) critério(s) abaixo: </TextTypography>
            {criteriasData.map((criteria, index) => {
              const isOpen = expanded === criteria.id;
              return (
                <Fragment key={criteria.id}>
                  <Box display="flex">
                    <FormControlLabel
                      sx={{ marginRight: 0 }}
                      control={
                        <Checkbox
                          checked={localSelected.includes(criteria.id)}
                          onChange={() => toggleSelection(criteria.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      onClick={(e) => e.stopPropagation()}
                      label=""
                    />
                    <AccordionCriteria
                      title={`Critério ${index + 1}`}
                      openAccordion={isOpen}
                      setOpenAccordion={() =>
                        setExpanded(isOpen ? null : criteria.id)
                      }
                    />
                  </Box>
                  {isOpen && (
                    <Typography sx={{ ml: 2, mb: 2, textAlign: 'justify' }}>
                      {criteria.description}
                    </Typography>
                  )}
                </Fragment>
              );
            })}
          </CriteriasListBox>
          <OutlineButton
            onClick={() => setShowNewCriteria(!showNewCriteria)}
            disabled={submitLoading}
            sx={{ m: '0 8px 20px' }}
          >
            {IconAddMS}novo critério
          </OutlineButton>
          {showNewCriteria && (
            <UploadBox>
              <CriteriaText>Novo critério</CriteriaText>
              <TextField
                id="description"
                label="descrição"
                value={description}
                disabled={submitLoading}
                required
                multiline
                rows={3}
                onChange={(e) => setDescription(e.target.value)}
                sx={textFieldDefaultStyle}
              />
              <CriteriaText>Adicionar documentos:</CriteriaText>
              <Grid container spacing={2} alignItems="center">
                {documentName.map((doc, i) => (
                  <Grid
                    item
                    xs={4}
                    display="flex"
                    alignContent="center"
                    alignItems="center"
                    gap="4px"
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                  >
                    <TextField
                      id={`document-name-${i}`}
                      label="nome do documento"
                      value={doc.name}
                      inputProps={{ maxLength: 60 }}
                      disabled={submitLoading}
                      onChange={(e) => {
                        const newDocs = [...documentName];
                        newDocs[i] = { ...newDocs[i], name: e.target.value };
                        setDocumentName(newDocs);
                      }}
                      sx={{
                        ...textFieldDefaultStyle,
                        '& .MuiInputBase-input': { padding: '14px' },
                      }}
                    />
                    <IconButton
                      onClick={() => {
                        const newDocs = [...documentName];
                        newDocs.splice(i, 1);
                        setDocumentName(newDocs);
                      }}
                      disabled={submitLoading}
                      size="small"
                      sx={{ color: 'error.main', height: '34px' }}
                    >
                      {IconDeleteMS}
                    </IconButton>
                  </Grid>
                ))}
                <Grid item xs={1}>
                  <ActionButton
                    onClick={() =>
                      setDocumentName([
                        ...documentName,
                        { name: '', document_type: NoticeDocumentType.OTHERS },
                      ])
                    }
                    disabled={submitLoading}
                    sx={{ marginTop: 0 }}
                  >
                    {IconAddMS}
                  </ActionButton>
                </Grid>
              </Grid>
              <Box display="flex" gap={4}>
                <OutlineButton
                  disabled={submitLoading}
                  onClick={() => resetNewCriteria()}
                >
                  cancelar
                </OutlineButton>
                <SubmitButton disabled={submitLoading} onClick={handleSubmit}>
                  salvar
                </SubmitButton>
              </Box>
            </UploadBox>
          )}
          <FlexReverseBox>
            <SaveButton onClick={handleConfirm}>salvar</SaveButton>
          </FlexReverseBox>
        </DialogContainer>
      </StyledDialog>
    </>
  );
}
